import { Component, Input, OnInit, effect, inject, signal, computed, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

import {
  EstablishmentsService,
  EstablishmentDetailsDto,
  UpdateInfoPayload,
  ReplaceHoursPayload,
  UpdateQueuePayload,
  DayDto,
  QueueRowDto,
  PATIENT_LANGUAGES,
} from '../../core/services/establishments.service';
import { SessionService } from '../../core/session.service';
import { API_BASE_URL } from '../../core/api-base-url.token';
import { ToastService } from '../../core/services/toast.service';
import { SupportService } from '../../core/services/support.service';
import { flagIcon } from '../../core/utils/flag-icon.util';

// Modales
import { EstabCreateModalComponent } from '../../component/estab-create-modal/estab-create-modal.component';
import { QueueCreateModalComponent } from '../../component/queue-create-modal/queue-create-modal.component';
import { HorairesComponent } from '../horaires/horaires.component';
import { InformationsComponent } from '../informations/informations.component';
import { PageSiteSwitcherComponent } from '../../component/page-site-switcher/page-site-switcher.component';

/** Horaires : un créneau simple */
type TimeSlot = { open: string; close: string };

@Component({
  selector: 'app-etablissement',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, EstabCreateModalComponent, QueueCreateModalComponent, HorairesComponent, InformationsComponent, PageSiteSwitcherComponent, TranslatePipe],
  templateUrl: './etablissement.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./etablissement.component.scss'],
})
export class EtablissementComponent implements OnInit {
  @Input() inline = false;

  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private api = inject(EstablishmentsService);
  private session = inject(SessionService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private supportService = inject(SupportService);
  /** Base d'URL dynamique (local/dev/prod) */
  private base = inject(API_BASE_URL);

  id: string | null = null;

  loading = signal(true);
  savingInfo = signal(false);
  error = signal<string | null>(null);

  dto = signal<EstablishmentDetailsDto | null>(null);
  queues = signal<QueueRowDto[]>([]);

  /** Sélection de la file dans la liste déroulante */
  selectedQueueId = signal<string | null>(null);
  selectedQueue = computed(() => this.queues().find(q => q.id === this.selectedQueueId()) || null);

  /** --- Modale de suppression --- */
  deleteConfirmOpen = signal(false);
  deleteTarget = signal<QueueRowDto | null>(null);

  estabDeleteOpen = signal(false);
  showContactModal = signal(false);
  contactModalType = signal<'creation' | 'deletion'>('deletion');

  /** Modale création file */
  queueCreateOpen = signal(false);

  /** Sous-page horaires */
  showHorairesSubpage = signal(false);

  /** Sous-page informations */
  showInformationsSubpage = signal(false);

  /** Form établissement */
  infoForm = this.fb.nonNullable.group({
    name: ['', [Validators.required]],
    langues: [['fr'], [Validators.required]],
    tz: ['Europe/Paris', [Validators.required]],
    address: ['' as string | null],
    place_id: ['' as string | null],
    website: ['' as string | null],
    show_patient_reason: [false],
    show_end_prestation: [false],
  });

  /** Snapshots pour savoir si c'est modifié */
  private infoInitial: { name: string; langues: string[]; tz: string; address: string; place_id: string; website: string; show_patient_reason: boolean; show_end_prestation: boolean } = { name: '', langues: ['fr'], tz: 'Europe/Paris', address: '', place_id: '', website: '', show_patient_reason: false, show_end_prestation: false };
  private hoursInitial: DayDto[] = [];
  private queueInitial: Record<string, { name: string; patient_message: string | null; name_i18n: any; patient_message_i18n: any }> = {};

  /** map id -> FormGroup (name + patient_message + i18n) */
  queueForms: Record<string, FormGroup> = {};

  // ===================== HORAIRES (UI) =====================
  public schedules: TimeSlot[][] = [
    [{ open: '07:30', close: '12:30' }, { open: '14:00', close: '19:30' }],
    [{ open: '07:30', close: '12:30' }, { open: '14:00', close: '19:30' }],
    [{ open: '07:30', close: '12:30' }, { open: '14:00', close: '19:30' }],
    [{ open: '07:30', close: '12:30' }, { open: '14:00', close: '19:30' }],
    [{ open: '07:30', close: '12:30' }, { open: '14:00', close: '19:30' }],
    [{ open: '07:30', close: '12:30' }, { open: '14:00', close: '19:30' }],
    [],
  ];
  public daysEnabled: boolean[] = [true, true, true, true, true, true, false];

  // ===================== Toast ORA =====================
  toastVisible = false;
  toastSubtitle = '';
  private toastTimer: any;
  private showToast(subtitle: string) {
    this.toastSubtitle = subtitle;
    this.toastVisible = true;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => (this.toastVisible = false), 2200);
  }

  /** Quand on ouvre un jour sans créneau, ajouter 07:30–12:30 */
  onToggleDay(index: number, checked: boolean): void {
    this.daysEnabled[index] = checked;
    if (checked) {
      const arr = this.schedules[index] ?? [];
      if (!arr.length) {
        this.schedules[index] = [{ open: '07:30', close: '12:30' }];
      }
    }
  }

  addSlot(dayIndex: number): void {
    (this.schedules[dayIndex] ??= []).push({ open: '14:00', close: '19:30' });
  }
  removeLastSlot(dayIndex: number): void {
    const arr = this.schedules[dayIndex];
    if (arr?.length) arr.pop();
  }

  onTimeChange(dayIndex: number, slotIndex: number, field: 'open' | 'close', value: string): void {
    const slot = this.schedules[dayIndex]?.[slotIndex];
    if (slot) {
      slot[field] = this.toHHmm(value);
    }
  }

  isSlotInvalid(dayIndex: number, slotIndex: number): boolean {
    const slot = this.schedules[dayIndex]?.[slotIndex];
    if (!slot) return false;

    // Vérifier que l'heure d'ouverture est avant l'heure de fermeture
    if (slot.open >= slot.close) return true;

    // Vérifier les chevauchements avec les autres créneaux du même jour
    const daySlots = this.schedules[dayIndex] || [];
    for (let i = 0; i < daySlots.length; i++) {
      if (i === slotIndex) continue;
      const otherSlot = daySlots[i];
      if (this.slotsOverlap(slot, otherSlot)) return true;
    }

    return false;
  }

  private slotsOverlap(slot1: TimeSlot, slot2: TimeSlot): boolean {
    return slot1.open < slot2.close && slot2.open < slot1.close;
  }

  // ===================== Réactivité à la sélection =====================
  private selectedEffect = effect(
    () => {
      const selId = this.session.selectedId();
      if (!selId) {
        this.error.set('Aucun établissement sélectionné.');
        this.loading.set(false);
        return;
      }
      if (this.id !== selId) {
        this.id = selId;
        this.load();
      }
    },
    { allowSignalWrites: true },
  );

  ngOnInit(): void {
    const qpId = this.route.snapshot.queryParamMap.get('estab');
    if (qpId && this.session.selectedId() !== qpId) this.session.selectEstablishment(qpId);

    window.addEventListener('showEstabContactModal', () => {
      this.contactModalType.set('creation');
      this.showContactModal.set(true);
    });
  }

  // ===================== Actions API =====================
  /**
   * Recharge l'établissement. Si `preferQueueId` est fourni et présent dans la réponse,
   * la sélection de la file tombera dessus.
   */
  load(preferQueueId?: string): void {
    if (!this.id) return;
    this.loading.set(true);
    this.error.set(null);

    // mémoriser la queue sélectionnée (ou préférée si fournie)
    const prevQueueId = preferQueueId ?? this.selectedQueueId();

    this.api.getDetails(this.id).subscribe({
      next: (dto) => {
        this.dto.set(dto);

        // Établissement
        this.infoForm.reset({
          name: dto.name ?? '',
          langues: dto.langues ?? ['fr'],
          tz: dto.tz ?? 'Europe/Paris',
          address: dto.address ?? '',
          place_id: dto.place_id ?? '',
          website: dto.website ?? '',
          show_patient_reason: dto.show_patient_reason ?? false,
          show_end_prestation: dto.show_end_prestation ?? false,
        });
        this.infoInitial = {
          name: dto.name ?? '',
          langues: dto.langues ?? ['fr'],
          tz: dto.tz ?? 'Europe/Paris',
          address: dto.address ?? '',
          place_id: dto.place_id ?? '',
          website: dto.website ?? '',
          show_patient_reason: dto.show_patient_reason ?? false,
          show_end_prestation: dto.show_end_prestation ?? false,
        };

        // Files
        const qs = dto.queues ?? [];
        this.queues.set(qs);
        this.queueForms = {};
        this.queueInitial = {};
        const langues = dto.langues ?? ['fr'];
        for (const q of qs) {
          const nameI18n = this.parseI18n(q.name_i18n);
          const msgI18n = this.parseI18n(q.patient_message_i18n);
          const init = {
            name: q.name ?? '',
            patient_message: q.patient_message ?? null,
            name_i18n: nameI18n,
            patient_message_i18n: msgI18n
          };
          this.queueInitial[q.id] = init;

          const formGroup: any = {
            name: [init.name, [Validators.required]],
            patient_message: [init.patient_message ?? '']
          };

          // Ajouter les champs i18n pour chaque langue
          langues.forEach(lang => {
            if (lang !== 'fr') {
              formGroup[`name_${lang}`] = [nameI18n[lang] || ''];
              formGroup[`patient_message_${lang}`] = [msgI18n[lang] || ''];
            }
          });

          this.queueForms[q.id] = this.fb.nonNullable.group(formGroup) as FormGroup;
        }

        // Sélection : priorité à preferQueueId s'il existe dans la liste
        if (preferQueueId && qs.some((q) => q.id === preferQueueId)) {
          this.selectedQueueId.set(preferQueueId);
        } else if (prevQueueId && qs.some((q) => q.id === prevQueueId)) {
          this.selectedQueueId.set(prevQueueId);
        } else {
          this.selectedQueueId.set(qs[0]?.id ?? null);
        }

        // Horaires
        this.hydrateHoursFromDto(dto.opening_hours ?? []);
        this.hoursInitial = this.normalizeHours(dto.opening_hours ?? []);

        this.loading.set(false);
      },
      error: (err) => {
        console.error('[Etablissement] getDetails error', err);
        this.error.set('Impossible de charger les détails.');
        this.loading.set(false);
      },
    });
  }

  private shallowEqual(a: any, b: any): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  private normalizeHours(days: DayDto[]): DayDto[] {
    const norm: DayDto[] = [];
    for (const d of days || []) {
      const wd = Number(d.weekday) || 1;
      const slots = (d.slots || [])
        .map((s) => ({ start: this.toHHmm(s.start), end: this.toHHmm(s.end) }))
        .filter((s) => s.start && s.end);
      if (slots.length) norm.push({ weekday: wd, slots });
    }
    norm.sort((a, b) => a.weekday - b.weekday);
    return norm;
  }

  private canSaveInfo(): boolean {
    const raw = this.infoForm.getRawValue();
    const cur = { name: raw.name ?? '', langues: raw.langues ?? ['fr'], tz: raw.tz ?? 'Europe/Paris', address: raw.address ?? '', place_id: raw.place_id ?? '', website: raw.website ?? '', show_patient_reason: raw.show_patient_reason ?? false, show_end_prestation: raw.show_end_prestation ?? false };
    return !this.shallowEqual(cur, this.infoInitial) && this.infoForm.valid;
  }
  private canSaveSelectedQueue(): boolean {
    const qid = this.selectedQueueId();
    if (!qid) return false;
    const fg = this.queueForms[qid];
    if (!fg || fg.invalid) return false;
    const raw = fg.getRawValue();
    const langues = this.infoForm.get('langues')?.value ?? ['fr'];

    const nameI18n: any = {};
    const msgI18n: any = {};
    langues.forEach(lang => {
      if (lang !== 'fr') {
        nameI18n[lang] = raw[`name_${lang}`] || '';
        msgI18n[lang] = raw[`patient_message_${lang}`] || '';
      }
    });

    const cur = {
      name: (raw.name ?? '').trim(),
      patient_message: raw.patient_message?.trim() || null,
      name_i18n: nameI18n,
      patient_message_i18n: msgI18n
    };
    return !this.shallowEqual(cur, this.queueInitial[qid] ?? {});
  }
  private canSaveHours(): boolean {
    // Vérifier qu'il n'y a pas d'erreurs de validation
    for (let i = 0; i < 7; i++) {
      if (!this.daysEnabled[i]) continue;
      const slots = this.schedules[i] || [];
      for (let j = 0; j < slots.length; j++) {
        if (this.isSlotInvalid(i, j)) return false;
      }
    }

    const cur = this.normalizeHours(this.buildHoursPayload());
    return !this.shallowEqual(cur, this.hoursInitial);
  }

  // --------- Save actions ----------
  saveInfo(): void {
    if (!this.id || this.infoForm.invalid) {
      this.showToast('Formulaire invalide');
      return;
    }
    if (!this.canSaveInfo()) {
      this.showToast('Aucune modification à enregistrer');
      return;
    }

    const raw = this.infoForm.getRawValue();
    const payload: UpdateInfoPayload = {
      name: raw.name ?? undefined,
      langues: raw.langues ?? undefined,
      tz: raw.tz ?? undefined,
      address: raw.address ?? undefined,
      place_id: raw.place_id ?? undefined,
      website: raw.website ?? undefined,
      show_patient_reason: raw.show_patient_reason ?? undefined,
      show_end_prestation: raw.show_end_prestation ?? undefined,
    };

    this.savingInfo.set(true);
    this.api.updateInfo(this.id, payload).subscribe({
      next: () => {
        this.showToast('Établissement enregistré');
        this.load();
      },
      error: (err) => {
        console.error('[Etablissement] updateInfo error', err);
        this.savingInfo.set(false);
      },
      complete: () => this.savingInfo.set(false),
    });
  }

  saveOpeningHours(): void {
    if (!this.id) return;
    if (!this.canSaveHours()) {
      this.showToast('Aucune modification à enregistrer');
      return;
    }

    const payload: ReplaceHoursPayload = { opening_hours: this.buildHoursPayload() };
    this.api.replaceOpeningHours(this.id, payload).subscribe({
      next: () => {
        this.showToast('Horaires enregistrés');
        this.load();
      },
      error: (err) => {
        const msg = err?.error?.message || 'Erreur lors de enregistrement des horaires';
        this.showToast(msg);
        console.error('[Etablissement] replaceOpeningHours error', err);
      },
    });
  }

  saveSelectedQueue(): void {
    const qid = this.selectedQueueId();
    if (!qid) return;
    const fg = this.queueForms[qid];
    if (!fg || fg.invalid) {
      this.showToast('Formulaire invalide');
      return;
    }
    if (!this.canSaveSelectedQueue()) {
      this.showToast('Aucune modification à enregistrer');
      return;
    }

    const raw = fg.getRawValue();
    const langues = this.infoForm.get('langues')?.value ?? ['fr'];

    const payload: UpdateQueuePayload = {
      name: raw.name ?? undefined,
      patient_message: raw.patient_message?.trim() || null
    };

    // Ajouter i18n seulement si plusieurs langues
    if (langues.length > 1) {
      const nameI18n: any = { fr: raw.name };
      const msgI18n: any = { fr: raw.patient_message || '' };

      langues.forEach(lang => {
        if (lang !== 'fr') {
          nameI18n[lang] = raw[`name_${lang}`] || '';
          msgI18n[lang] = raw[`patient_message_${lang}`] || '';
        }
      });

      payload.name_i18n = JSON.stringify(nameI18n);
      payload.patient_message_i18n = JSON.stringify(msgI18n);
    }

    this.api.updateQueue(qid, payload).subscribe({
      next: () => {
        this.showToast('File enregistrée');
        this.load(qid);
      },
      error: (err) => console.error('[Etablissement] updateQueue error', err),
    });
  }

  // ========= Suppression établissement =========
  openEstabDelete(): void {
    if (!this.id) return;
    this.estabDeleteOpen.set(true);
    document.body.style.overflow = 'hidden';
    setTimeout(() => (document.querySelector('.modal') as HTMLElement | null)?.focus());
  }
  closeEstabDelete(): void {
    this.estabDeleteOpen.set(false);
    document.body.style.overflow = '';
  }
  confirmEstabDelete(): void {
    const id = this.id;
    if (!id) return;
    this.api.deleteEstablishment(id).subscribe({
      next: () => {
        this.closeEstabDelete();
        this.showToast('Établissement supprimé');
        this.session.afterEstablishmentDeleted(id);
        if (!this.session.selectedId()) {
          this.router.navigate(['/']);
        } else {
          this.id = this.session.selectedId();
          this.load();
        }
      },
      error: (err) => {
        console.error('[Etablissement] deleteEstablishment error', err);
        this.closeEstabDelete();
        const isAccessDenied = err?.status === 403 ||
                               err?.error?.error === 'access_denied' ||
                               (err?.error?.message && err.error.message.toLowerCase().includes('administrateur'));
        if (isAccessDenied) {
          this.contactModalType.set('deletion');
          this.showContactModal.set(true);
        } else {
          let title = err?.error?.title || 'Erreur';
          const message = err?.error?.message || 'Suppression impossible';
          this.toastService.show(title, 'error', 5000, message);
        }
      }
    });
  }



  /** ——— Suppression de file (UI) ——— */
  onDeleteQueue(): void {
    const q = this.selectedQueue();
    if (!q) return;
    this.deleteTarget.set(q);
    this.deleteConfirmOpen.set(true);
    document.body.style.overflow = 'hidden';
    setTimeout(() => (document.querySelector('.modal') as HTMLElement | null)?.focus());
  }

  closeDeleteConfirm(): void {
    this.deleteConfirmOpen.set(false);
    this.deleteTarget.set(null);
    document.body.style.overflow = '';
  }

  confirmDelete(): void {
    const q = this.deleteTarget();
    if (!q) return;
    this.api.deleteQueue(q.id).subscribe({
      next: () => {
        this.showToast(`File « ${q.name} » supprimée`);
        this.closeDeleteConfirm();
        this.load();
        this.session.bumpDataVersion();
      },
      error: (err) => {
        console.error('[Etablissement] deleteQueue error', err);
        this.closeDeleteConfirm();
        let title = err?.error?.title || 'Erreur';
        if (err?.error?.error === 'access_denied') title = 'Accès refusé';
        if (err?.error?.error === 'erreur_interne') title = 'Accès refusé';
        const message = err?.error?.message || 'Suppression impossible';
        this.toastService.show(title, 'error', 5000, message);
      },
    });
  }

  /** force "HH:mm" */
  private toHHmm(v: string | null | undefined): string {
    if (!v) return '';
    const s = String(v).trim();
    const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (!m) return s;
    const hh = String(Math.min(23, Math.max(0, +m[1]))).padStart(2, '0');
    const mm = String(Math.min(59, Math.max(0, +m[2]))).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  /** Remplit daysEnabled + schedules à partir du DTO de la BDD */
  private hydrateHoursFromDto(days: DayDto[]): void {
    this.daysEnabled = [false, false, false, false, false, false, false];
    this.schedules = [[], [], [], [], [], [], []];
    for (const d of days || []) {
      const idx = (Number(d.weekday) || 1) - 1;
      if (idx < 0 || idx > 6) continue;
      const slots = (d.slots || [])
        .map((s) => ({ open: this.toHHmm(s.start), close: this.toHHmm(s.end) }))
        .filter((s) => s.open && s.close)
        .sort((a, b) => a.open.localeCompare(b.open));
      this.daysEnabled[idx] = slots.length > 0;
      this.schedules[idx] = slots;
    }
  }

  /** Construit le payload DayDto[] pour le back (HH:mm) */
  private buildHoursPayload(): DayDto[] {
    const out: DayDto[] = [];
    for (let i = 0; i < 7; i++) {
      if (!this.daysEnabled[i]) continue;
      const slots = (this.schedules[i] || [])
        .map((s) => ({ start: this.toHHmm(s.open), end: this.toHHmm(s.close) }))
        .filter((s) => s.start && s.end)
        .sort((a, b) => a.start.localeCompare(b.start));
      if (slots.length) out.push({ weekday: i + 1, slots });
    }
    return out;
  }

  // --- ouverture/fermeture modale création établissement
  createOpen = signal(false);
  openCreate(): void { this.createOpen.set(true); }
  closeCreate(): void { this.createOpen.set(false); }

  onCreatedEstab(e: { id: string }): void {
    if (!e?.id) return;
    this.session.refreshEstablishments();
    setTimeout(() => this.session.selectEstablishment(e.id), 100);
    this.id = e.id;
    this.createOpen.set(false);
    this.load();
    this.showToast('Établissement créé');
  }

  // --- ouverture/fermeture modale création file
  openQueueCreate(): void {
    if (!this.id) return;
    this.queueCreateOpen.set(true);
    document.body.style.overflow = 'hidden';
    setTimeout(() => (document.getElementById('acc-root') as HTMLElement | null)?.focus());
  }
  closeQueueCreate(): void {
    this.queueCreateOpen.set(false);
    document.body.style.overflow = '';
  }

  /** ✅ Après création : recharge + sélectionne directement la nouvelle file */
  onCreatedQueue(e: { id: string; name?: string }): void {
    if (!e?.id) return;
    this.showToast('File créée');
    this.closeQueueCreate();
    this.load(e.id);                 // ⬅️ priorise la sélection de la nouvelle file
    this.session.bumpDataVersion();  // notifie l'accueil
  }

  getCarouselOffset(): number {
    const queues = this.queues();
    const selectedId = this.selectedQueueId();
    const index = queues.findIndex(q => q.id === selectedId);
    if (index === -1) return 0;
    const itemWidth = 200;
    const containerWidth = window.innerWidth - 32;
    const offset = Math.max(0, (index * itemWidth) - (containerWidth / 2) + (itemWidth / 2));
    return -offset;
  }

  openHorairesSubpage(): void {
    this.showHorairesSubpage.set(true);
  }

  closeHorairesSubpage(): void {
    this.showHorairesSubpage.set(false);
  }

  openInformationsSubpage(): void {
    console.log('Ouverture de la page Informations');
    this.showInformationsSubpage.set(true);
  }

  closeInformationsSubpage(): void {
    this.showInformationsSubpage.set(false);
  }

  readonly flagIcon = flagIcon;
  addLangMenuOpen = signal(false);

  get remainingPatientLanguages() {
    const current = this.infoForm.get('langues')?.value ?? [];
    return PATIENT_LANGUAGES.filter(l => !current.includes(l.code));
  }

  toggleAddLangMenu(): void {
    if (this.remainingPatientLanguages.length === 0) return;
    this.addLangMenuOpen.update(v => !v);
  }

  selectLanguageToAdd(lang: string): void {
    const current = this.infoForm.get('langues')?.value || [];
    if (!current.includes(lang)) {
      this.infoForm.patchValue({ langues: [...current, lang] });
      this.infoForm.markAsDirty();

      // Ajouter les champs i18n manquants aux formulaires de queues déjà construits
      // (sinon [formControlName]="'name_' + lang" pointe vers un contrôle inexistant → crash)
      Object.keys(this.queueForms).forEach(qid => {
        const fg = this.queueForms[qid];
        if (!fg.contains(`name_${lang}`)) fg.addControl(`name_${lang}`, this.fb.nonNullable.control(''));
        if (!fg.contains(`patient_message_${lang}`)) fg.addControl(`patient_message_${lang}`, this.fb.nonNullable.control(''));
      });
    }
    this.addLangMenuOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClickCloseLangMenu(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.addLangMenuOpen() && !target.closest('.lang-add-wrap')) {
      this.addLangMenuOpen.set(false);
    }
  }

  removeLanguage(lang: string): void {
    const current = this.infoForm.get('langues')?.value || [];
    if (current.length > 1) {
      this.infoForm.patchValue({ langues: current.filter((l: string) => l !== lang) });
      this.infoForm.markAsDirty();

      // Supprimer les champs i18n des formulaires de queues
      Object.keys(this.queueForms).forEach(qid => {
        const fg = this.queueForms[qid];
        if (fg.contains(`name_${lang}`)) fg.removeControl(`name_${lang}`);
        if (fg.contains(`patient_message_${lang}`)) fg.removeControl(`patient_message_${lang}`);
      });
    }
  }

  private parseI18n(i18nStr: string | null | undefined): any {
    if (!i18nStr) return {};
    try {
      return JSON.parse(i18nStr);
    } catch {
      return {};
    }
  }

  getLanguages(): string[] {
    return this.infoForm.get('langues')?.value ?? ['fr'];
  }

  getLanguageLabel(code: string): string {
    return PATIENT_LANGUAGES.find(l => l.code === code)?.label ?? code;
  }

  closeContactModal(): void {
    this.showContactModal.set(false);
  }

  contactSupport(): void {
    if (this.contactModalType() === 'creation') {
      this.contactSupportForCreation();
    } else {
      this.contactSupportForDeletion();
    }
  }

  contactSupportForDeletion(): void {
    const establishmentName = this.infoForm.get('name')?.value || 'Non spécifié';
    const establishmentId = this.id || '';

    const payload: any = {
      object: 'establishment_deletion',
      message: `Demande de suppression de l'établissement: ${establishmentName}`
    };

    if (establishmentId) {
      payload.establishment_id = establishmentId;
    }

    this.supportService.sendMessage(payload).subscribe({
      next: (response) => {
        const ticketInfo = response.ticket_id ? ` (Réf: ${response.ticket_id})` : '';
        this.toastService.show('✓ Demande envoyée', 'success', 4000, `Notre équipe vous contactera rapidement${ticketInfo}`);
        this.showContactModal.set(false);
      },
      error: () => {
        this.toastService.show('Erreur', 'error', 5000, 'Impossible d\'envoyer la demande');
      }
    });
  }

  contactSupportForCreation(): void {
    const payload = {
      object: 'establishment_creation',
      message: 'Demande de création d\'un nouvel établissement'
    };

    this.supportService.sendMessage(payload).subscribe({
      next: (response) => {
        const ticketInfo = response.ticket_id ? ` (Réf: ${response.ticket_id})` : '';
        this.toastService.show('✓ Demande envoyée', 'success', 4000, `Notre équipe vous contactera rapidement${ticketInfo}`);
        this.showContactModal.set(false);
      },
      error: () => {
        this.toastService.show('Erreur', 'error', 5000, 'Impossible d\'envoyer la demande');
      }
    });
  }
}
